import dayjs from "dayjs";

import {
  type CheckoutFunnelCart,
  type CheckoutFunnelIssueKind,
  type CheckoutFunnelIssueRow,
  type CheckoutFunnelSegment,
  type CheckoutFunnelStats,
  type CheckoutFunnelSteps,
  toCheckoutCartId,
} from "@fitness-app/data-models/entities/CheckoutFunnelStats";

/** Wartość filtra dla segmentów bez wersji (koszyk bez testu albo zdarzenie sprzed rejestracji). */
export const NO_VARIANT = "__none__";

export interface CheckoutFunnelFilters {
  /** Id koszyka z `toCheckoutCartId`, a nie ścieżka: jeden wariant ma kilka adresów. */
  cart?: string;
  checkoutVariant?: string;
  lpVariant?: string;
}

export interface CheckoutFunnelOrders {
  paid: number;
  failed: number;
  canceled: number;
  pending: number;
  revenue: number;
}

export interface CheckoutFunnelDay extends CheckoutFunnelSteps, CheckoutFunnelOrders {
  day: string;
}

export interface CheckoutFunnelIssueSummary {
  key: string;
  kind: CheckoutFunnelIssueKind;
  detail: string | null;
  code: string | null;
  events: number;
  persons: number;
}

const STEP_KEYS = [
  "entries",
  "fieldCompleted",
  "methodSelected",
  "payClicked",
  "payBlocked",
  "blikPending",
  "blikTimeout",
  "orderFailed",
  "paymentFailed",
] as const satisfies readonly (keyof CheckoutFunnelSteps)[];

const ORDER_KEYS = [
  "paid",
  "failed",
  "canceled",
  "pending",
  "revenue",
] as const satisfies readonly (keyof CheckoutFunnelOrders)[];

const emptyDay = (day: string): CheckoutFunnelDay => ({
  day,
  ...(Object.fromEntries(STEP_KEYS.map((key) => [key, 0])) as Record<(typeof STEP_KEYS)[number], number>),
  ...(Object.fromEntries(ORDER_KEYS.map((key) => [key, 0])) as Record<(typeof ORDER_KEYS)[number], number>),
});

const matchesVariant = (value: string | null, filter: string | undefined) =>
  !filter || (filter === NO_VARIANT ? value === null : value === filter);

export const matchesCheckoutFunnelFilters = (segment: CheckoutFunnelSegment, filters: CheckoutFunnelFilters) =>
  (!filters.cart || toCheckoutCartId(segment.cart) === filters.cart) &&
  matchesVariant(segment.checkoutVariant, filters.checkoutVariant) &&
  matchesVariant(segment.lpVariant, filters.lpVariant);

const listDays = (from: string, to: string): string[] => {
  const days: string[] = [];
  for (let day = dayjs(from); !day.isAfter(dayjs(to), "day"); day = day.add(1, "day")) {
    days.push(day.format("YYYY-MM-DD"));
  }
  return days;
};

/**
 * Dzień po dniu, od najnowszego. Kroki to osoby danego dnia, więc suma dni
 * liczy dwa razy kogoś, kto wrócił następnego dnia.
 */
export const buildCheckoutFunnelDays = (
  stats: CheckoutFunnelStats,
  filters: CheckoutFunnelFilters,
  range: { from: string; to: string },
): CheckoutFunnelDay[] => {
  const days = new Map(listDays(range.from, range.to).map((day) => [day, emptyDay(day)]));

  for (const row of stats.steps) {
    const day = days.get(row.day);
    if (!day || !matchesCheckoutFunnelFilters(row, filters)) continue;
    STEP_KEYS.forEach((key) => {
      day[key] += row[key];
    });
  }
  for (const row of stats.orders) {
    const day = days.get(row.day);
    if (!day || !matchesCheckoutFunnelFilters(row, filters)) continue;
    ORDER_KEYS.forEach((key) => {
      day[key] += row[key];
    });
  }

  return [...days.values()].reverse();
};

export const sumCheckoutFunnelDays = (days: CheckoutFunnelDay[]): CheckoutFunnelDay =>
  days.reduce((total, day) => {
    [...STEP_KEYS, ...ORDER_KEYS].forEach((key) => {
      total[key] += day[key];
    });
    total.revenue = Math.round(total.revenue * 100) / 100;
    return total;
  }, emptyDay("total"));

export const summarizeCheckoutFunnelIssues = (
  issues: CheckoutFunnelIssueRow[],
  filters: CheckoutFunnelFilters,
): CheckoutFunnelIssueSummary[] => {
  const summary = new Map<string, CheckoutFunnelIssueSummary>();
  for (const issue of issues) {
    if (!matchesCheckoutFunnelFilters(issue, filters)) continue;
    const key = [issue.kind, issue.detail, issue.code].join("|");
    const entry = summary.get(key) ?? {
      key,
      kind: issue.kind,
      detail: issue.detail,
      code: issue.code,
      events: 0,
      persons: 0,
    };
    entry.events += issue.events;
    entry.persons += issue.persons;
    summary.set(key, entry);
  }
  return [...summary.values()].sort((first, second) => second.events - first.events);
};

export interface CheckoutFunnelCartOption extends CheckoutFunnelCart {
  entries: number;
}

export interface CheckoutFunnelCartGroup {
  productName: string | null;
  carts: CheckoutFunnelCartOption[];
}

/**
 * Koszyki pogrupowane po produkcie: wszystko, co miało ruch albo zamówienia
 * w zakresie, plus aktywne lejki i warianty z katalogu, nawet bez wejść.
 * W grupie od najczęściej odwiedzanego, grupy od największego ruchu.
 */
export const groupCheckoutFunnelCarts = (stats: CheckoutFunnelStats): CheckoutFunnelCartGroup[] => {
  const catalog = new Map(stats.carts.map((cart) => [cart.id, cart]));
  const options = new Map<string, CheckoutFunnelCartOption>();
  const option = (id: string, path?: string): CheckoutFunnelCartOption => {
    const existing = options.get(id);
    if (existing) return existing;
    const created = {
      ...(catalog.get(id) ?? {
        id,
        kind: id.startsWith("path:") ? ("path" as const) : (id.split(":")[0] as CheckoutFunnelCart["kind"]),
        slug: id.startsWith("path:") ? (path ?? id) : id.slice(id.indexOf(":") + 1),
        productName: null,
        status: null,
      }),
      entries: 0,
    };
    options.set(id, created);
    return created;
  };

  stats.steps.forEach((row) => {
    option(toCheckoutCartId(row.cart), row.cart).entries += row.entries;
  });
  stats.orders.forEach((row) => option(toCheckoutCartId(row.cart), row.cart));
  stats.carts.filter((cart) => cart.status === "active").forEach((cart) => option(cart.id));

  const groups = new Map<string | null, CheckoutFunnelCartGroup>();
  for (const cart of options.values()) {
    const group = groups.get(cart.productName) ?? { productName: cart.productName, carts: [] };
    group.carts.push(cart);
    groups.set(cart.productName, group);
  }
  const groupEntries = (group: CheckoutFunnelCartGroup) => group.carts.reduce((sum, cart) => sum + cart.entries, 0);
  return [...groups.values()]
    .map((group) => ({
      ...group,
      carts: group.carts.sort(
        (first, second) => second.entries - first.entries || first.slug.localeCompare(second.slug),
      ),
    }))
    .sort(
      (first, second) =>
        groupEntries(second) - groupEntries(first) ||
        (first.productName ?? "~").localeCompare(second.productName ?? "~", "pl"),
    );
};

export const listCheckoutFunnelVariants = (
  stats: CheckoutFunnelStats,
  key: "checkoutVariant" | "lpVariant",
  cart?: string,
): (string | null)[] => {
  const values = new Set<string | null>();
  [...stats.steps, ...stats.orders].forEach((row) => {
    if (!cart || toCheckoutCartId(row.cart) === cart) values.add(row[key]);
  });
  return [...values].sort((first, second) => (first ?? "").localeCompare(second ?? ""));
};

/** Udział kroku w wejściach; `null`, gdy nie było wejść. */
export const stepRate = (value: number, entries: number): number | null =>
  entries > 0 ? Math.round((value / entries) * 1000) / 10 : null;
